import React, { useState, useRef, useEffect, useCallback } from 'react';
import { personelAPI } from '../utils/api';

const styles = {
  wrapper: { position: 'relative' },
  input: {
    width: '100%', padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8,
    fontSize: 13, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s',
  },
  inputFocus: { borderColor: '#2563EB' },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
    background: 'white', border: '1.5px solid #E5E7EB', borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto', marginTop: 4,
  },
  item: {
    padding: '8px 12px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #F3F4F6',
    display: 'flex', flexDirection: 'column', gap: 2,
  },
  itemHover: { background: '#EFF6FF' },
  nrp: { fontFamily: 'monospace', fontWeight: 700, color: '#1E40AF', fontSize: 12 },
  nama: { fontWeight: 600, color: '#111827', fontSize: 12 },
  meta: { color: '#6B7280', fontSize: 11 },
  card: {
    marginTop: 8, padding: 10, background: '#F0FDF4', borderRadius: 8, fontSize: 12,
    display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #BBF7D0',
  },
  cardNrp: { fontFamily: 'monospace', fontWeight: 700, color: '#059669', fontSize: 13 },
  cardNama: { fontWeight: 600, color: '#111827' },
  cardMeta: { color: '#6B7280', fontSize: 11 },
  clearBtn: {
    marginLeft: 'auto', background: 'none', border: 'none', color: '#EF4444',
    cursor: 'pointer', fontSize: 16, fontWeight: 700, padding: '0 4px', lineHeight: 1,
  },
  loading: { padding: '12px', textAlign: 'center', color: '#9CA3AF', fontSize: 12 },
  empty: { padding: '12px', textAlign: 'center', color: '#9CA3AF', fontSize: 12 },
};

export default function NrpLookup({ value, onSelect, onClear, required, disabled, placeholder }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(-1);
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef(null);
  const timerRef = useRef(null);

  const search = useCallback(async (term) => {
    if (!term || term.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await personelAPI.getAll(`?search=${encodeURIComponent(term)}&limit=10`);
      setResults(res.data || []);
      setOpen(true);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setHoverIdx(-1);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(v), 300);
  };

  const handleSelect = (p) => {
    setQuery('');
    setOpen(false);
    setResults([]);
    onSelect && onSelect({ nrp: p.nrp, nama: p.nama, pangkat: p.pangkat, corps: p.corps, kesatuan: p.kesatuan, jabatan: p.jabatan });
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    onClear && onClear();
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHoverIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHoverIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && hoverIdx >= 0) { e.preventDefault(); handleSelect(results[hoverIdx]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (value) {
    return (
      <div style={styles.card}>
        <div>
          <div style={styles.cardNrp}>{value.nrp}</div>
          <div style={styles.cardNama}>{value.nama}</div>
          <div style={styles.cardMeta}>{[value.pangkat, value.corps, value.kesatuan].filter(Boolean).join(' | ')}</div>
        </div>
        {!disabled && <button type="button" style={styles.clearBtn} onClick={handleClear} title="Hapus">&times;</button>}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} style={styles.wrapper}>
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder || 'Cari NRP atau Nama...'}
        disabled={disabled}
        required={required}
        style={{ ...styles.input, ...(focused ? styles.inputFocus : {}) }}
      />
      {open && (
        <div style={styles.dropdown}>
          {loading && <div style={styles.loading}>Mencari...</div>}
          {!loading && results.length === 0 && <div style={styles.empty}>Tidak ditemukan</div>}
          {!loading && results.map((p, i) => (
            <div
              key={p.nrp}
              style={{ ...styles.item, ...(hoverIdx === i ? styles.itemHover : {}) }}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseDown={() => handleSelect(p)}
            >
              <div><span style={styles.nrp}>{p.nrp}</span> &mdash; <span style={styles.nama}>{p.nama}</span></div>
              <div style={styles.meta}>{[p.pangkat, p.corps, p.kesatuan, p.jabatan].filter(Boolean).join(' | ')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
